// web/src/screens/admin/sessions_list.ts
//
// The admin "Game log" — a paginated list of game sessions. This is the
// /admin landing screen (restored v1 behaviour) and also serves /admin/sessions.
//
// v2-aligned rebuild of v1's /admin/sessions table. Columns: Started (link →
// session detail), Game (title), Cards (distinct), Correct/Partial/Incorrect/
// Silence, Cost (microUSD), Status. Pagination via ?offset= (fixed limit 50).
//
// All API-supplied text via textContent only (T-11-09a).

import { renderAdminShell } from "./admin_shell.js";
import { adminGet } from "../../api/admin.js";
import type { AdminSessionListDTO, AdminSessionSummaryDTO } from "../../types/api.js";
import { navigateAdmin, fmtMicrousd, fmtTimestamp, statusPill, table, adminLink, errMessage } from "./logs_common.js";

const PAGE_SIZE = 50;

export function mount(root: HTMLElement, _props?: unknown): () => void {
  // Section landing — breadcrumb is just the section name (the top nav in
  // renderAdminShell handles cross-section navigation; the breadcrumb is the
  // within-section path).
  const main = renderAdminShell(root, [{ label: "Game log" }]);

  const offset = readOffset();

  const status = document.createElement("p");
  status.textContent = "Loading…";
  status.setAttribute("style", "color: #6b7280; font-size: 14px;");
  main.appendChild(status);

  const tableWrap = document.createElement("div");
  tableWrap.setAttribute("style", "overflow-x: auto;");
  main.appendChild(tableWrap);

  const pager = document.createElement("div");
  pager.setAttribute("style", "display: flex; gap: 12px; align-items: center; margin-top: 16px; font-size: 14px;");
  main.appendChild(pager);

  function render(list: AdminSessionListDTO): void {
    tableWrap.replaceChildren();
    pager.replaceChildren();

    if (list.items.length === 0) {
      status.textContent = offset === 0 ? "No sessions yet." : "No sessions on this page.";
    } else {
      status.textContent = `${list.total} session${list.total === 1 ? "" : "s"} total — showing ${offset + 1}–${offset + list.items.length}.`;
      const t = table(["Started", "Game", "Cards", "Correct", "Partial", "Incorrect", "Silence", "Cost", "Status"]);
      for (const s of list.items) t.addRow(rowCells(s));
      tableWrap.appendChild(t.el);
    }

    // Pager: Prev (offset>0), Next (offset+items < total).
    const prevDisabled = offset === 0;
    const nextDisabled = offset + list.items.length >= list.total;
    pager.appendChild(pagerButton("‹ Previous", prevDisabled, () => navigateAdmin(`/admin/sessions?offset=${Math.max(0, offset - PAGE_SIZE)}`)));
    const pageInfo = document.createElement("span");
    pageInfo.textContent = `Page ${Math.floor(offset / PAGE_SIZE) + 1} of ${Math.max(1, Math.ceil(list.total / PAGE_SIZE))}`;
    pageInfo.setAttribute("style", "color: #6b7280;");
    pager.appendChild(pageInfo);
    pager.appendChild(pagerButton("Next ›", nextDisabled, () => navigateAdmin(`/admin/sessions?offset=${offset + PAGE_SIZE}`)));
  }

  async function load(): Promise<void> {
    try {
      const list = await adminGet<AdminSessionListDTO>(`/api/admin/sessions?limit=${PAGE_SIZE}&offset=${offset}`);
      render(list);
    } catch (err) {
      status.textContent = errMessage(err, "load sessions");
    }
  }

  void load();

  // No persistent listeners are installed on `root`; the pager/adminLink
  // listeners die with the DOM nodes on the next dispatch's replaceChildren.
  return () => {};
}

function rowCells(s: AdminSessionSummaryDTO): Array<string | Node> {
  return [
    adminLink(fmtTimestamp(s.started_at), `/admin/sessions/${encodeURIComponent(s.id)}`),
    s.game_title || s.game_short_code,
    String(s.distinct_cards),
    String(s.correct_count),
    String(s.partial_count),
    String(s.incorrect_count),
    String(s.silence_count),
    fmtMicrousd(s.total_cost_microusd),
    statusPill(s.status),
  ];
}

function readOffset(): number {
  const raw = new URLSearchParams(window.location.search).get("offset");
  if (!raw) return 0;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function pagerButton(label: string, disabled: boolean, onClick: () => void): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.textContent = label;
  b.disabled = disabled;
  b.setAttribute(
    "style",
    disabled
      ? "padding: 6px 12px; background: #f3f4f6; color: #9ca3af; border: 1px solid #e5e7eb; border-radius: 4px; cursor: not-allowed; font-size: 14px;"
      : "padding: 6px 12px; background: white; color: #1d4ed8; border: 1px solid #d1d5db; border-radius: 4px; cursor: pointer; font-size: 14px;",
  );
  if (!disabled) b.addEventListener("click", onClick);
  return b;
}
